import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Calendar, MapPin, Users, Image, ArrowLeft, ArrowRight, Check, Plus } from 'lucide-react';
import { Card, Button, Input, useToast } from '@caruso/ui';
import { eventsService, CreateEventDto, UpdateEventDto } from '@/services/events.service';
import { listsService } from '@/services/lists.service';
import { clientsService } from '@/services/clients.service';

const step1Schema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  description: z.string().optional(),
  eventDate: z.string().min(1, 'Data é obrigatória'),
  location: z.string().optional(),
});

type Step1Data = z.infer<typeof step1Schema>;

export function EventWizard() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<CreateEventDto>>({});
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [createNewList, setCreateNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string>('');

  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch existing event if editing
  const { data: existingEvent, isLoading: isLoadingEvent } = useQuery({
    queryKey: ['event', id],
    queryFn: () => eventsService.getById(id!),
    enabled: isEditMode,
  });

  // Populate form with existing event data
  useEffect(() => {
    if (existingEvent && isEditMode) {
      setFormData({
        name: existingEvent.name,
        description: existingEvent.description,
        eventDate: existingEvent.eventDate,
        location: existingEvent.location,
        status: existingEvent.status,
      });
      if (existingEvent.listId) {
        setSelectedListId(existingEvent.listId);
      }
      if (existingEvent.bannerUrl) {
        setBannerPreview(existingEvent.bannerUrl);
      }
    }
  }, [existingEvent, isEditMode]);

  const { register, handleSubmit, formState: { errors } } = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: formData,
  });

  // Queries
  const { data: lists } = useQuery({
    queryKey: ['lists'],
    queryFn: () => listsService.getAll({ page: 1, limit: 100, activeOnly: true }),
    enabled: currentStep === 3,
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsService.getAll({ page: 1, pageSize: 100 }),
    enabled: currentStep === 3 && createNewList,
  });

  // Mutation para criar evento
  const createEventMutation = useMutation({
    mutationFn: async (data: CreateEventDto) => {
      let finalData = { ...data };

      // Upload banner se houver
      if (bannerFile) {
        const { url } = await eventsService.uploadBanner(bannerFile);
        finalData.bannerUrl = url;
      }

      // Criar lista se necessário
      if (createNewList && newListName) {
        const newList = await listsService.create({
          name: newListName,
          description: `Lista criada para o evento ${data.name}`,
        });
        finalData.listId = newList.id;

        // Adicionar clientes à nova lista
        if (selectedClients.length > 0) {
          await listsService.addMembers(newList.id, selectedClients);
        }
      } else if (selectedListId) {
        finalData.listId = selectedListId;
      }

      return eventsService.create(finalData);
    },
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast({
        title: 'Evento criado!',
        description: 'Evento criado com sucesso',
        variant: 'success',
      });
      navigate(`/app/eventos/${event.id}/convidados`);
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao criar evento',
        variant: 'destructive',
      });
    },
  });

  // Mutation para atualizar evento
  const updateEventMutation = useMutation({
    mutationFn: async (data: UpdateEventDto) => {
      let finalData = { ...data };

      // Upload banner se houver novo arquivo
      if (bannerFile) {
        const { url } = await eventsService.uploadBanner(bannerFile);
        finalData.bannerUrl = url;
      }

      // Atualizar listId se alterado
      if (selectedListId && selectedListId !== existingEvent?.listId) {
        finalData.listId = selectedListId;
      }

      return eventsService.update(id!, finalData);
    },
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Evento atualizado!',
        description: 'Evento atualizado com sucesso',
        variant: 'success',
      });
      navigate(`/app/eventos/${event.id}/convidados`);
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar evento',
        variant: 'destructive',
      });
    },
  });

  const onStep1Submit = (data: Step1Data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setCurrentStep(2);
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFinish = () => {
    if (isEditMode) {
      updateEventMutation.mutate(formData as UpdateEventDto);
    } else {
      createEventMutation.mutate(formData as CreateEventDto);
    }
  };

  const steps = [
    { number: 1, title: 'Informações Básicas', icon: Calendar },
    { number: 2, title: 'Banner', icon: Image },
    { number: 3, title: 'Convidados', icon: Users },
    { number: 4, title: 'Revisão', icon: Check },
  ];

  if (isLoadingEvent) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-brand-gold text-xl mb-4">Carregando evento...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/eventos')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-4xl font-bold text-white">
            {isEditMode ? 'Editar Evento' : 'Criar Novo Evento'}
          </h1>
          <p className="text-brand-ivory/80 mt-1">Passo {currentStep} de 4</p>
        </div>
      </div>

      {/* Steps */}
      <Card className="p-6 bg-brand-ink/50 border-brand-gold/20">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;

            return (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                    isCompleted ? 'bg-brand-gold border-brand-gold' :
                    isActive ? 'border-brand-gold bg-brand-gold/20' :
                    'border-brand-gold/30 bg-brand-ink'
                  }`}>
                    {isCompleted ? <Check className="w-6 h-6 text-brand-dark" /> : <Icon className="w-6 h-6 text-brand-gold" />}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${isActive || isCompleted ? 'text-brand-gold' : 'text-brand-ivory/50'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-4 ${isCompleted ? 'bg-brand-gold' : 'bg-brand-gold/20'}`} />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Step Content */}
      <Card className="p-8 bg-brand-ink/50 border-brand-gold/20">
        {/* Step 1: Informações Básicas */}
        {currentStep === 1 && (
          <form onSubmit={handleSubmit(onStep1Submit)} className="space-y-8">
            <div>
              <label className="block text-base font-semibold text-brand-gold mb-3">Nome do Evento *</label>
              <Input
                {...register('name')}
                placeholder="Ex: Festa de Aniversário"
                className="text-base"
                style={{
                  colorScheme: 'dark',
                  color: 'rgb(244, 241, 233) !important' as any,
                  WebkitTextFillColor: 'rgb(244, 241, 233)',
                }}
              />
              {errors.name && <p className="text-red-400 text-base mt-2">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-base font-semibold text-brand-gold mb-3">Descrição</label>
              <textarea
                {...register('description')}
                placeholder="Descreva o evento..."
                className="w-full p-4 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory text-base placeholder:text-brand-ivory/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50 resize-none"
                style={{
                  colorScheme: 'dark',
                  color: 'rgb(244, 241, 233) !important' as any,
                  WebkitTextFillColor: 'rgb(244, 241, 233)',
                }}
                rows={5}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-base font-semibold text-brand-gold mb-3">Data e Hora *</label>
                <Input
                  type="datetime-local"
                  {...register('eventDate')}
                  className="text-base"
                  style={{
                    colorScheme: 'dark',
                    color: 'rgb(244, 241, 233) !important' as any,
                    WebkitTextFillColor: 'rgb(244, 241, 233)',
                  }}
                />
                {errors.eventDate && <p className="text-red-400 text-base mt-2">{errors.eventDate.message}</p>}
              </div>

              <div>
                <label className="block text-base font-semibold text-brand-gold mb-3">Local</label>
                <Input
                  {...register('location')}
                  placeholder="Ex: Salão de Festas"
                  className="text-base"
                  style={{
                    colorScheme: 'dark',
                    color: 'rgb(244, 241, 233) !important' as any,
                    WebkitTextFillColor: 'rgb(244, 241, 233)',
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" className="px-8 py-3 text-base">
                Próximo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </form>
        )}

        {/* Step 2: Banner */}
        {currentStep === 2 && (
          <div className="space-y-8">
            <div>
              <label className="block text-base font-semibold text-brand-gold mb-3">Banner do Evento</label>
              <div className="border-2 border-dashed border-brand-gold/30 rounded-lg p-12 text-center">
                {bannerPreview ? (
                  <div className="space-y-6">
                    <img src={bannerPreview} alt="Preview" className="max-h-80 mx-auto rounded-lg" />
                    <Button variant="outline" onClick={() => { setBannerFile(null); setBannerPreview(''); }} className="px-6 py-3 text-base">
                      Remover Imagem
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Image className="w-20 h-20 text-brand-gold/50 mx-auto mb-6" />
                    <p className="text-brand-ivory/70 mb-6 text-base">Arraste uma imagem ou clique para selecionar</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerChange}
                      className="hidden"
                      id="banner-upload"
                    />
                    <label htmlFor="banner-upload" className="cursor-pointer">
                      <Button type="button" className="px-6 py-3 text-base">Selecionar Imagem</Button>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(1)} className="px-8 py-3 text-base">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <Button onClick={() => setCurrentStep(3)} className="px-8 py-3 text-base">
                Próximo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Convidados */}
        {currentStep === 3 && (
          <div className="space-y-8">
            <div>
              <label className="block text-base font-semibold text-brand-gold mb-3">Lista de Convidados</label>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <Button
                    variant={!createNewList ? 'default' : 'outline'}
                    onClick={() => setCreateNewList(false)}
                    className="flex-1 py-3 text-base"
                  >
                    Usar Lista Existente
                  </Button>
                  <Button
                    variant={createNewList ? 'default' : 'outline'}
                    onClick={() => setCreateNewList(true)}
                    className="flex-1 py-3 text-base"
                  >
                    Criar Nova Lista
                  </Button>
                </div>

                {!createNewList ? (
                  <select
                    value={selectedListId}
                    onChange={(e) => setSelectedListId(e.target.value)}
                    className="w-full p-4 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory text-base focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50"
                    style={{
                      colorScheme: 'dark',
                      color: 'rgb(244, 241, 233) !important' as any,
                      WebkitTextFillColor: 'rgb(244, 241, 233)',
                    }}
                  >
                    <option value="">Selecione uma lista</option>
                    {lists?.data.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-brand-gold mb-2">Nome da Nova Lista</label>
                      <Input
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        placeholder="Nome da nova lista"
                        className="text-base"
                        style={{
                          colorScheme: 'dark',
                          color: 'rgb(244, 241, 233) !important' as any,
                          WebkitTextFillColor: 'rgb(244, 241, 233)',
                        }}
                      />
                    </div>
                    <div className="border border-brand-gold/20 rounded-lg p-5 max-h-80 overflow-y-auto bg-brand-smoke/30">
                      <p className="text-brand-gold font-semibold mb-4 text-base">Selecionar Clientes</p>
                      <div className="space-y-2">
                        {clients?.data.map((client) => (
                          <label key={client.id} className="flex items-center gap-3 p-3 hover:bg-brand-ink/50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedClients.includes(client.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClients([...selectedClients, client.id]);
                                } else {
                                  setSelectedClients(selectedClients.filter(id => id !== client.id));
                                }
                              }}
                              className="w-5 h-5"
                            />
                            <span className="text-brand-ivory text-base">{client.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(2)} className="px-8 py-3 text-base">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <Button onClick={() => setCurrentStep(4)} className="px-8 py-3 text-base">
                Próximo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Revisão */}
        {currentStep === 4 && (
          <div className="space-y-8">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-brand-gold font-serif">Revisão do Evento</h3>

              <div className="grid gap-6">
                <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                  <span className="text-brand-ivory/60 text-sm font-medium">Nome do Evento</span>
                  <p className="text-brand-gold font-semibold text-xl mt-1">{formData.name}</p>
                </div>

                {formData.description && (
                  <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                    <span className="text-brand-ivory/60 text-sm font-medium">Descrição</span>
                    <p className="text-brand-ivory text-base mt-2">{formData.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                    <span className="text-brand-ivory/60 text-sm font-medium">Data e Hora</span>
                    <p className="text-brand-ivory font-semibold text-lg mt-1">
                      {formData.eventDate && format(new Date(formData.eventDate), "dd/MM/yyyy 'às' HH:mm")}
                    </p>
                  </div>

                  {formData.location && (
                    <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                      <span className="text-brand-ivory/60 text-sm font-medium">Local</span>
                      <p className="text-brand-ivory text-base mt-1">{formData.location}</p>
                    </div>
                  )}
                </div>

                {bannerPreview && (
                  <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                    <span className="text-brand-ivory/60 text-sm font-medium block mb-3">Banner do Evento</span>
                    <img src={bannerPreview} alt="Banner" className="max-h-48 rounded-lg" />
                  </div>
                )}

                <div className="bg-brand-smoke/30 rounded-lg p-5 border border-brand-gold/10">
                  <span className="text-brand-ivory/60 text-sm font-medium">Lista de Convidados</span>
                  <p className="text-brand-ivory text-base mt-1">
                    {createNewList ? `Nova lista: ${newListName} (${selectedClients.length} convidados)` :
                     selectedListId ? lists?.data.find(l => l.id === selectedListId)?.name : 'Nenhuma lista selecionada'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(3)} className="px-8 py-3 text-base">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <Button
                onClick={handleFinish}
                disabled={createEventMutation.isPending || updateEventMutation.isPending}
                className="px-8 py-3 text-base"
              >
                {createEventMutation.isPending || updateEventMutation.isPending
                  ? (isEditMode ? 'Atualizando...' : 'Criando...')
                  : (isEditMode ? 'Atualizar Evento' : 'Criar Evento')}
                <Check className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
